import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService, LoginResponse } from 'angular-auth-oidc-client';
import { Observable, map, filter, switchMap, of } from 'rxjs';

export interface UserInfo {
  sub: string;
  name?: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
}

/** Session storage key of the URL to return to after the Keycloak login. */
const RETURN_URL_KEY = 'redirectUrl';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oidcSecurityService = inject(OidcSecurityService);
  private router = inject(Router);

  // Observable indicating whether user is authenticated
  isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    map((result) => result.isAuthenticated)
  );

  // Observable with current access token
  accessToken$ = this.oidcSecurityService.getAccessToken();

  // Observable with current ID token
  idToken$ = this.oidcSecurityService.getIdToken();

  // Observable with user information from ID token
  userData$: Observable<UserInfo | null> = this.oidcSecurityService.userData$.pipe(
    map((result) => result.userData as UserInfo | null)
  );

  // Check authentication status and handle callback
  checkAuth(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth();
  }

  /**
   * Starts the Keycloak login (full page redirect) and remembers where to come back
   * afterwards; CallbackComponent navigates there via consumeReturnUrl(). Defaults to
   * the current URL, so a login forced by an expired session in the middle of the
   * flow (see authInterceptor) keeps deep links such as /building/{id} or
   * /map?siteId=...&building=... intact. The legacy ADFS login did the same with its
   * responseUri parameter.
   */
  login(returnUrl: string = this.currentUrl()): void {
    const url = returnUrl && !returnUrl.startsWith('/AuthenticationCallback') ? returnUrl : '/';
    sessionStorage.setItem(RETURN_URL_KEY, url);
    console.log('[AuthService] Starting login, return URL:', url);
    this.oidcSecurityService.authorize();
  }

  /** URL remembered by login(), or '/' when there is none. Cleared on read. */
  consumeReturnUrl(): string {
    const url = sessionStorage.getItem(RETURN_URL_KEY) || '/';
    sessionStorage.removeItem(RETURN_URL_KEY);
    return url;
  }

  // Logout user (redirects to Keycloak logout)
  logout(): void {
    this.oidcSecurityService.logoff().subscribe();
  }

  // Get access token synchronously (for HTTP interceptor)
  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  // Get ID token (for NavVis IVION API)
  getIdToken(): Observable<string> {
    return this.oidcSecurityService.getIdToken();
  }

  // Force token refresh
  refreshToken(): Observable<LoginResponse> {
    return this.oidcSecurityService.forceRefreshSession();
  }

  // Get authorization header for API calls
  getAuthorizationHeader(): Observable<Record<string, string>> {
    return this.accessToken$.pipe(
      map((token) => (token ? { Authorization: `Bearer ${token}` } : {}))
    );
  }

  // Check if user has specific role (from Keycloak token)
  hasRole(role: string): Observable<boolean> {
    return this.userData$.pipe(
      map((userData) => {
        if (!userData) return false;
        // Keycloak stores roles in realm_access or resource_access claims
        const roles = (userData as any)?.realm_access?.roles || [];
        return roles.includes(role);
      })
    );
  }

  /**
   * URL of the current view. The router URL is preferred because it still carries
   * the deep link parameters after the IVION SDK has rewritten the address bar;
   * while the first navigation is still running it is just '/', then the address
   * bar (e.g. /building/3192) is the better source.
   */
  private currentUrl(): string {
    const routerUrl = this.router.url;
    if (routerUrl && routerUrl !== '/') {
      return routerUrl;
    }
    return window.location.pathname + window.location.search;
  }
}
